import { useEffect, useRef } from "react";
import {
  getLocalJWT,
  getLocalUserId,
} from "../functions/utilities";
import { socketInit } from "../Hooks/socketInit";
import { Debt } from "../../interfaces/interfaces";

type DebtSocketPayload = {
  formId: number;
  debtId: string;
  jobType: string;
  debt: Debt;
};

// every event carries the full updated debt, so one handler covers them all
const DEBT_EVENTS = [
  "DEBT_UPLOAD_ACCEPTED",
  "DEBT_PROCESSING_STARTED",
  "DEBT_PROCESSING_COMPLETE",
  "DEBT_PROCESSING_FAILED",
  "DEBT_UPDATED",
];

// Mirrors useBankStatementsSocket: own connection, joins the user room the
// backend emits DEBT_* events to, and hands each updated debt to the caller.
// onReconnect runs after the socket comes back from a drop, so the caller can
// fetch anything that finished during the gap; the first connect doesn't call
// it, since the caller loads on mount.
export function useDebtSocket(
  onDebtEvent: (debt: Debt) => void,
  onReconnect?: () => void
) {
  const handlerRef = useRef(onDebtEvent);
  handlerRef.current = onDebtEvent;
  const reconnectRef = useRef(onReconnect);
  reconnectRef.current = onReconnect;

  useEffect(() => {
    const userId = getLocalUserId();
    const token = getLocalJWT();
    if (!userId || !token) return;

    let socket: ReturnType<typeof socketInit>;
    try {
      socket = socketInit(userId.toString(), token);
    } catch {
      return;
    }

    let hasDisconnected = false;

    const joinUserRoom = () => {
      socket.emit("JOIN_USER_ROOM", { roomId: `user_${userId}` });
    };
    const handleConnect = () => {
      joinUserRoom();
      if (hasDisconnected) reconnectRef.current?.();
    };
    const markDisconnected = () => {
      hasDisconnected = true;
    };
    const handleDebtEvent = (payload: DebtSocketPayload) => {
      if (payload?.debt) handlerRef.current(payload.debt);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", markDisconnected);
    socket.on("connect_error", markDisconnected);
    DEBT_EVENTS.forEach((event) => socket.on(event, handleDebtEvent));
    if (socket.connected) joinUserRoom();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", markDisconnected);
      socket.off("connect_error", markDisconnected);
      DEBT_EVENTS.forEach((event) => socket.off(event, handleDebtEvent));
      socket.disconnect();
    };
  }, []);
}
